import { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

interface Project {
  id: number;
  title: string;
  category: string;
  description: string;
  imageSrc: string;
}

const Projects = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [activeFilter, setActiveFilter] = useState('all');
  const [activeProject, setActiveProject] = useState<Project | null>(null);

  const projectFilters = [
    { id: 'all', label: 'All Projects' },
    { id: 'decks', label: 'Decks' },
    { id: 'pergolas', label: 'Pergolas' },
    { id: 'patios', label: 'Patio Covers' },
    { id: 'kitchens', label: 'Outdoor Kitchens' },
  ];

  const projectsData: Project[] = [
    {
      id: 1,
      title: 'Modern Luxury Deck',
      category: 'decks',
      description: 'A contemporary deck design featuring clean lines, premium composite materials, and seamless integration with the natural surroundings.',
      imageSrc: '/images/Screenshot 2025-04-01 202717.png',
    },
    {
      id: 2,
      title: 'Elegant Pergola Retreat',
      category: 'pergolas',
      description: 'A sophisticated pergola design that combines traditional craftsmanship with modern amenities, creating the perfect outdoor living space.',
      imageSrc: '/images/Screenshot 2025-04-01 202707.png',
    },
    {
      id: 3,
      title: 'Gourmet Outdoor Kitchen',
      category: 'kitchens',
      description: 'A fully equipped outdoor kitchen featuring premium appliances, weather-resistant materials, and elegant design elements.',
      imageSrc: '/images/Screenshot 2025-04-01 202657.png',
    },
    {
      id: 4,
      title: 'Multi-Level Entertainment Deck',
      category: 'decks',
      description: 'A stunning multi-tiered deck perfect for entertaining, featuring built-in seating and panoramic lake views.',
      imageSrc: '/images/Screenshot 2025-04-01 202726.png',
    },
    {
      id: 5,
      title: 'Custom Shade Pergola',
      category: 'pergolas',
      description: 'An architecturally striking pergola design with adjustable shade features and integrated lighting system.',
      imageSrc: '/images/IMG_1682.jpeg',
    },
    {
      id: 6,
      title: 'Covered Patio Oasis',
      category: 'patios',
      description: 'A luxurious covered patio space that seamlessly blends indoor comfort with outdoor living.',
      imageSrc: '/images/IMG_1588.jpeg',
    },
    {
      id: 7,
      title: 'Premium View Deck',
      category: 'decks',
      description: 'A premium deck installation with glass railings and high-end finishes, maximizing the stunning valley views.',
      imageSrc: '/images/IMG_1820.jpeg',
    },
    {
      id: 8,
      title: 'Complete Outdoor Living',
      category: 'kitchens',
      description: 'A comprehensive outdoor living space featuring a kitchen, dining area, and lounge space perfect for year-round enjoyment.',
      imageSrc: '/images/Screenshot 2025-04-01 202754.png',
    },
  ];

  const filteredProjects = activeFilter === 'all'
    ? projectsData
    : projectsData.filter(project => project.category === activeFilter);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        damping: 15,
        stiffness: 100
      }
    },
  };

  return (
    <section id="projects" className="section bg-white">
      <div className="container">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ 
            duration: 0.6,
            type: "spring",
            damping: 15,
            stiffness: 100
          }}
          className="text-center mb-12"
        >
          <h2 className="section-title">Our Featured Projects</h2>
          <p className="section-subtitle mx-auto">
            Explore our portfolio of custom outdoor living spaces that have transformed homes across the region.
          </p>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="flex flex-wrap justify-center gap-3 mt-8 mb-12"
          >
            {projectFilters.map(filter => (
              <motion.button
                key={filter.id}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setActiveFilter(filter.id)}
                className={`px-5 py-2 rounded-full transition-all ${
                  activeFilter === filter.id
                    ? 'bg-primary text-white'
                    : 'bg-light hover:bg-accent/20'
                }`}
              >
                {filter.label}
              </motion.button>
            ))}
          </motion.div>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {filteredProjects.map(project => (
            <motion.div
              key={project.id}
              variants={itemVariants}
              className="group relative overflow-hidden rounded-lg shadow-lg cursor-pointer"
              onClick={() => setActiveProject(project)}
              whileHover={{ y: -10 }}
            >
              <motion.div 
                initial={{ scale: 1.2 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.8 }}
                className="relative h-80 overflow-hidden"
              >
                <motion.img
                  src={project.imageSrc}
                  alt={project.title}
                  className="w-full h-full object-cover"
                  whileHover={{ scale: 1.1 }}
                  transition={{ duration: 0.4 }}
                />
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.6 }}
                  whileHover={{ opacity: 0.8 }}
                  transition={{ duration: 0.3 }}
                  className="absolute inset-0 bg-gradient-to-t from-dark/80 to-transparent"
                ></motion.div>
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                  className="absolute bottom-0 left-0 p-6 z-10"
                >
                  <h3 className="text-xl font-bold text-white mb-1">
                    {project.title}
                  </h3>
                </motion.div>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>

        {activeProject && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-dark/90 z-50 flex items-center justify-center p-4"
            onClick={() => setActiveProject(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 15 }}
              className="bg-white rounded-lg max-w-4xl w-full overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              <motion.div 
                initial={{ scale: 1.1 }}
                animate={{ scale: 1 }}
                className="relative h-80"
              >
                <img
                  src={activeProject.imageSrc}
                  alt={activeProject.title}
                  className="w-full h-full object-cover"
                />
              </motion.div>
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="p-6"
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-2xl font-bold text-dark">
                      {activeProject.title}
                    </h3>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setActiveProject(null)}
                    className="text-dark hover:text-primary"
                  >
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </motion.button>
                </div>
                <p className="text-gray-700 mb-6">{activeProject.description}</p>
                <div className="flex justify-end">
                  <motion.a 
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    href="#contact" 
                    className="btn btn-primary"
                  >
                    Start a Similar Project
                  </motion.a>
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="mt-12 text-center"
        >
          <motion.a 
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            href="#contact" 
            className="btn btn-primary"
          >
            Discuss Your Project
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default Projects; 